/**
 * `LearnMoreDialog` — the in-app explainer dialog opened from the
 * `/agent-browser` bottombar's "Learn more" button. Covers what
 * Lexiun Browser is, what persists, how to use it, the personal
 * Google account trade-off, and the credential-sharing boundary.
 *
 * Manual overlay-+-modal pattern cloned verbatim from
 * `HowToUseLexiunDialog` (same coffee/cream palette, same Escape +
 * backdrop close, same Got-it CTA). Wider body (~36rem) and scrollable
 * because the content is longer; structured into 4 titled sections +
 * an opener paragraph.
 *
 * Accessibility: focus returns to the trigger button on close via the
 * `triggerRef` passed in by the parent — AgentBrowserLayout owns the
 * ref of its bottombar Learn-more button.
 */

import { useEffect, type RefObject } from "react";
import { Lock, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface LearnMoreDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional ref to the trigger button. When provided, focus returns
   *  there on dialog close so keyboard users land back where they
   *  started. */
  triggerRef?: RefObject<HTMLButtonElement | null>;
}

/** Section ids — kept in lockstep with the i18n key tree under
 *  `agentBrowser.learnMore.sections.<id>.title` + `.body`. */
const SECTION_IDS = [
  "customize",
  "signInOnce",
  "googleAccount",
  "boundary",
] as const;

export function LearnMoreDialog({
  open,
  onClose,
  triggerRef,
}: LearnMoreDialogProps) {
  const { t } = useTranslation();

  // Close on Escape (mirrors HowToUseLexiunDialog).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus return on close — restore focus to the trigger so keyboard
  // navigation lands the user back at the Learn-more button.
  useEffect(() => {
    if (open) return;
    triggerRef?.current?.focus();
  }, [open, triggerRef]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label={t("agentBrowser.learnMore.close")}
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default bg-coffee-900/40 dark:bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="learn-more-dialog-title"
        data-testid="agent-browser-learn-more-dialog"
        className="fixed bottom-20 left-1/2 top-12 z-50 flex w-[48rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-cream-200 bg-cream-50 shadow-xl dark:border-coffee-700 dark:bg-coffee-900"
      >
        <div className="relative shrink-0 border-b border-cream-200/60 px-12 py-4 text-center dark:border-coffee-700/60">
          <h2
            id="learn-more-dialog-title"
            className="font-serif-display italic text-display-sm font-medium text-coffee-900 dark:text-cream-100"
          >
            {t("agentBrowser.learnMore.title")}
          </h2>
          <p
            data-testid="agent-browser-learn-more-subtitle"
            className="mt-1 font-serif-display italic text-body-sm text-gold-700 dark:text-gold-400"
          >
            {t("agentBrowser.learnMore.subtitle")}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("agentBrowser.learnMore.close")}
            className="absolute right-3 top-3 rounded p-1 text-cream-500 transition-colors hover:bg-cream-100/70 hover:text-coffee-700 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <p
            data-testid="agent-browser-learn-more-opening"
            className="text-body text-coffee-700 dark:text-cream-300"
          >
            {t("agentBrowser.learnMore.opening")}
          </p>
          <aside
            data-testid="agent-browser-learn-more-assurance"
            className="rounded-lg border-2 border-gold-500/40 bg-cream-100/60 px-4 py-3 text-center dark:border-gold-400/30 dark:bg-coffee-800/40"
          >
            <h3 className="flex items-center justify-center gap-2 text-body font-semibold text-coffee-900 dark:text-cream-100">
              <Lock
                size={16}
                className="text-gold-600 dark:text-gold-400"
                aria-hidden
              />
              {t("agentBrowser.learnMore.assurance.title")}
            </h3>
            <p className="mt-1.5 text-body-sm text-coffee-700 dark:text-cream-300">
              {t("agentBrowser.learnMore.assurance.body")}
            </p>
          </aside>
          {SECTION_IDS.map((id) => (
            <section key={id} data-testid={`agent-browser-learn-more-section-${id}`}>
              <h3 className="mb-1 text-body font-medium text-coffee-900 dark:text-cream-100">
                {t(`agentBrowser.learnMore.sections.${id}.title`)}
              </h3>
              <p className="text-body-sm text-coffee-700 dark:text-cream-300">
                {t(`agentBrowser.learnMore.sections.${id}.body`)}
              </p>
            </section>
          ))}
        </div>
        <div className="flex shrink-0 justify-end border-t border-cream-200/60 px-5 py-5 dark:border-coffee-700/60">
          <button
            type="button"
            onClick={onClose}
            data-testid="agent-browser-learn-more-cta"
            className="rounded-lg bg-coffee-800 px-3.5 py-1.5 text-sm font-medium text-cream-100 transition-colors hover:bg-coffee-700 dark:bg-cream-200 dark:text-coffee-900 dark:hover:bg-cream-100"
          >
            {t("agentBrowser.learnMore.close")}
          </button>
        </div>
      </div>
    </>
  );
}
